/**
 * hatchPattern — generate the pattern line families for a non-solid HATCH, as
 * RAW world-mm line segments laid across a boundary bbox (PDF-5a P2).
 *
 * We REUSE the scene's AutoCAD `.pat` definitions — `scene3d/hatch/
 * hatchPatterns.getPatternDefinition(name)` returns a `PatternDefinition` whose
 * `families: PatternFamily[]` carry the real `.pat` data (angle, origin, step
 * along, step across, dash list) for ANSI31..ANSI38, LINE, NET, NET3, SQUARE,
 * GRASS, ZIGZAG, EARTH, GRAVEL, BRICK plus any DXF custom pattern registered via
 * `setCustomPattern`. The scene uses these families to bake a tiled texture; we
 * instead lay infinite parallel lines per family and let the PDF backend CLIP
 * them to the boundary loops.
 *
 * ── Pattern coverage ───────────────────────────────────────────────────────
 * LINE-ACCURATE (real `.pat` families reused, including dash lists + double-line
 * offset families): every name in the scene's built-in table above + any custom
 * pattern registered through the scene cache. ANSI31 (45° lines) is the AutoCAD
 * default and is exact.
 *
 * GENERIC FALLBACK (single 45° solid family at the default 3.175-mm spacing):
 * any pattern name NOT in the table and NOT a registered custom pattern. This
 * keeps an unknown pattern VISIBLE as a recognisable cross-hatch rather than a
 * silent solid fill (CLAUDE.md PRIORITY #1: no faking) — the limit is documented
 * here + in the PR report. Full per-pattern `.pat` fidelity for arbitrary
 * acad.pat names is the same scope as the scene renderer (shared definition
 * table), so extending one extends both.
 */

import {
  getPatternDefinition,
  type PatternDefinition,
  type PatternFamily,
} from "../../../scene3d/hatch/hatchPatterns";
import type { HatchPatternLine, Point2 } from "../cadPrimitive";

/** Axis-aligned bbox in world mm (the pattern-line extent). */
export interface LoopBbox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * The scene texture uses a pattern-unit → texel scale (`PATTERN_UNIT_PX = 8`,
 * "one pattern unit = ~3 texels at scale 1"). For a vector plot the `.pat`
 * step values are already in drawing units (mm in paper space), so we apply
 * `patternScale` directly with NO texel divisor — a 3.175-unit ANSI31 step at
 * scale 1 plots 3.175 mm apart, matching AutoCAD's plotted hatch density.
 */
const GENERIC_SPACING_MM = 3.175;

/** Names AutoCAD uses for predefined GRADIENT hatches (DXF group 470). */
const GRADIENT_NAMES = new Set([
  "LINEAR",
  "CYLINDER",
  "INVCYLINDER",
  "SPHERICAL",
  "INVSPHERICAL",
  "HEMISPHERICAL",
  "INVHEMISPHERICAL",
  "CURVED",
  "INVCURVED",
]);

/**
 * Whether `patternName` is a predefined gradient name. Used by the converter to
 * route gradient hatches to the documented solid-colour fallback (the model
 * carries no gradient ramp — see hatchConverter MODEL GAP).
 */
export function isGradientPatternName(patternName: string): boolean {
  return GRADIENT_NAMES.has(patternName.trim().toUpperCase());
}

/**
 * Build the pattern line segments for a hatch across `bbox`. Returns RAW
 * world-mm segments (the page/VP transform applies downstream). The backend
 * clips them to the boundary loops, so we draw each family's lines fully across
 * the bbox diagonal extent.
 *
 * @param patternName  The hatch pattern (resolved through the scene `.pat` table).
 * @param patternScale Hatch pattern scale (DXF code 41); ≤0 → 1.
 * @param patternAngle Hatch rotation in DEGREES (DXF code 52) applied to every
 *                     family on top of its own `.pat` angle.
 * @param doubleLine   DXF code 77 — when true a perpendicular family is added
 *                     for any pattern that has a single family (AutoCAD "double").
 * @param bbox         Boundary bounding box (world mm).
 */
export function hatchPatternLines(
  patternName: string,
  patternScale: number,
  patternAngle: number,
  doubleLine: boolean,
  bbox: LoopBbox,
): HatchPatternLine[] {
  const scale = patternScale > 0 ? patternScale : 1;
  const globalRot = patternAngle * (Math.PI / 180);

  const def = resolveDefinition(patternName);
  const families = applyDoubleLine(def.families, doubleLine);

  const lines: HatchPatternLine[] = [];
  for (const family of families) {
    emitFamily(lines, family, scale, globalRot, bbox);
  }
  return lines;
}

/**
 * Resolve a pattern definition, reusing the scene's `.pat` table. Unknown names
 * fall back to a single 45° solid family (the documented generic fallback).
 */
function resolveDefinition(patternName: string): PatternDefinition {
  const def = getPatternDefinition(patternName);
  if (def && def.families.length > 0) return def;
  // SOLID has zero families in the scene table — but SOLID is routed to the
  // solid-fill branch before reaching here. Any other family-less / unknown
  // name uses the generic 45° fallback so the hatch stays visible.
  return {
    name: patternName,
    families: [
      {
        angleDeg: 45,
        originX: 0,
        originY: 0,
        stepAlong: 0,
        stepAcross: GENERIC_SPACING_MM,
        dashes: [],
      },
    ],
  };
}

/**
 * When `doubleLine` is set, add a perpendicular twin for every single-line
 * family (AutoCAD's "Double" hatch option crosses the pattern with a 90°
 * copy). Patterns that already have multiple families are unchanged.
 */
function applyDoubleLine(
  families: PatternFamily[],
  doubleLine: boolean,
): PatternFamily[] {
  if (!doubleLine || families.length === 0) return families;
  const out: PatternFamily[] = [];
  for (const f of families) {
    out.push(f);
    out.push({ ...f, angleDeg: f.angleDeg + 90 });
  }
  return out;
}

/**
 * Lay one family's parallel lines across the bbox. The lines run along the
 * family direction (global rotation + family angle) and are spaced `stepAcross`
 * (× scale) apart perpendicular to that direction. We cover the whole bbox by
 * iterating the perpendicular offset over the bbox's diagonal extent about its
 * centre, then drawing each line the full diagonal length (the clip trims it).
 */
function emitFamily(
  out: HatchPatternLine[],
  family: PatternFamily,
  scale: number,
  globalRot: number,
  bbox: LoopBbox,
): void {
  const stepAcross = family.stepAcross * scale;
  if (!Number.isFinite(stepAcross) || stepAcross <= 0) return;

  const angle = globalRot + family.angleDeg * (Math.PI / 180);
  const dirX = Math.cos(angle); // line direction (unit)
  const dirY = Math.sin(angle);
  const perpX = -dirY; // perpendicular (unit)
  const perpY = dirX;

  const cx = (bbox.minX + bbox.maxX) / 2;
  const cy = (bbox.minY + bbox.maxY) / 2;
  const w = bbox.maxX - bbox.minX;
  const h = bbox.maxY - bbox.minY;
  // Diagonal half-extent — covers the bbox at any rotation.
  const half = Math.hypot(w, h) / 2 + stepAcross;

  // Origin offset (along the perpendicular) so the pattern phase honours the
  // family's `.pat` origin. originX/originY are in pattern units (× scale);
  // project onto the perpendicular axis to get the across-offset phase.
  const originAcross =
    (family.originX * scale) * perpX + (family.originY * scale) * perpY;
  const phase = ((originAcross % stepAcross) + stepAcross) % stepAcross;

  const dashesMm = family.dashes.map((d) => d * scale);

  const lineCount = Math.ceil((half * 2) / stepAcross) + 1;
  for (let i = 0; i < lineCount; i++) {
    const offset = -half + phase + i * stepAcross;
    // Centre point of this line's perpendicular offset.
    const px = cx + perpX * offset;
    const py = cy + perpY * offset;
    const from: Point2 = { x: px - dirX * half, y: py - dirY * half };
    const to: Point2 = { x: px + dirX * half, y: py + dirY * half };
    out.push({ from, to, dashes: dashesMm });
  }
}
